import { Component } from '@theme/component';

/**
 * Live-chat availability indicator.
 *
 * Toggles between an "online" (green dot) and "offline" (gray dot) state based on a
 * per-weekday business-hours schedule in a fixed timezone, closing on public holidays.
 *
 * Holidays come from the free Nager.Date API (https://date.nager.at, no key required),
 * fetched per year and cached in localStorage so it's one request per visitor per year.
 * A manual `data-holidays` list adds custom closures the API doesn't know about (e.g. the
 * day after Thanksgiving). If the API is unreachable, only the manual list applies.
 *
 * The whole check runs client-side — never from server-rendered Liquid time — so it stays
 * correct regardless of full-page caching or the visitor's own timezone.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} label - The text node swapped between the online/offline labels.
 *
 * @typedef {object} DaySchedule
 * @property {number} open - Minutes since midnight the day opens (e.g. 9am = 540).
 * @property {number} close - Minutes since midnight the day closes (e.g. 6pm = 1080).
 *
 * @extends {Component<Refs>}
 */
class ChatStatus extends Component {
  requiredRefs = ['label'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  /** Closure dates (YYYY-MM-DD, ET): manual entries plus any fetched from the holiday API. */
  #holidays = new Set();

  /** Years whose holidays have already been loaded (or attempted), to avoid refetching. */
  #loadedYears = new Set();

  connectedCallback() {
    super.connectedCallback();

    // Seed with manually-listed custom closures (the holiday API won't include these).
    for (const date of (this.dataset.holidays || '').split(',')) {
      const trimmed = date.trim();
      if (trimmed) this.#holidays.add(trimmed);
    }

    this.#update();
    // Re-check every minute so the status flips at open/close without a page reload.
    this.#timer = setInterval(() => this.#update(), 60_000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearInterval(this.#timer);
  }

  #update() {
    const timezone = this.dataset.timezone || 'America/New_York';

    /** @type {Record<string, DaySchedule>} Keyed by short weekday name: Mon, Tue, … */
    const schedule = JSON.parse(this.dataset.schedule || '{}');

    // Read the current wall-clock time *in the configured timezone*, not the visitor's.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());

    const part = (type) => parts.find((entry) => entry.type === type)?.value ?? '';
    const weekday = part('weekday');
    const hour = Number(part('hour')) % 24;
    const minute = Number(part('minute'));
    const year = part('year');
    const date = `${year}-${part('month')}-${part('day')}`;
    const minutesNow = hour * 60 + minute;

    // Lazily pull this year's public holidays (handles New Year's rollover too).
    if (!this.#loadedYears.has(year)) this.#loadHolidays(year);

    const today = schedule[weekday];
    const isOnline =
      !this.#holidays.has(date) &&
      Boolean(today) &&
      minutesNow >= today.open &&
      minutesNow < today.close;

    this.dataset.state = isOnline ? 'online' : 'offline';
    this.refs.label.textContent = isOnline
      ? this.dataset.onlineLabel || 'Online'
      : this.dataset.offlineLabel || 'Offline';
  }

  /**
   * Load public holidays for a year from cache or the Nager.Date API, then refresh.
   * @param {string} year
   */
  async #loadHolidays(year) {
    // Mark up front so concurrent minute-ticks don't fire duplicate requests.
    this.#loadedYears.add(year);

    const country = this.dataset.country || 'US';
    const cacheKey = `chat-status:holidays:${country}:${year}`;

    /** @type {string[]} */
    let dates = [];
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        dates = JSON.parse(cached);
      } else {
        const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${country}`);
        if (!response.ok) return;
        // Keep only nationwide holidays (global: true) — this drops regional/observance
        // days like Good Friday or Lincoln's Birthday that the store stays open for.
        dates = (await response.json())
          .filter((holiday) => holiday.global)
          .map((holiday) => holiday.date);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(dates));
        } catch {
          // localStorage unavailable (private mode / quota) — fine, we just won't cache.
        }
      }
    } catch {
      // Network/parse failure — keep the manual list and don't retry this year.
      return;
    }

    for (const date of dates) this.#holidays.add(date);
    this.#update();
  }
}

customElements.define('chat-status', ChatStatus);
